import { useCallback, useEffect, useMemo, useRef } from "react"
import { meetings } from "services/api/meetingsClient"
import { saveStoredGuestSession } from "../shared/guestSession"
import { getPanelTokenFromUrl } from "../shared/panelUrls"
import { requiresMeetingAdmission, type CallSurfaceAttendee, type JoinInfo } from "../types"

type MainRoomTarget = Pick<JoinInfo, "url" | "token">
export type MainRoomResolveOptions = { forceRefresh?: boolean }
type Args = { meetingId: string; attendee: CallSurfaceAttendee; joinInfo: JoinInfo; onJoinInfoRefresh?: (info: JoinInfo) => void }

const hasReusableToken = (token: MainRoomTarget["token"]): boolean => {
  if (!token) return false
  try {
    const parts = token.split(".")
    if (parts.length !== 3) return false
    const payload = parts[1].replace(/-/g, "+").replace(/_/g, "/")
    const { exp } = JSON.parse(window.atob(payload.padEnd(Math.ceil(payload.length / 4) * 4, "=")))
    return typeof exp === "number" && Number.isFinite(exp) && exp * 1000 > Date.now() + 60_000
  } catch {
    return false
  }
}

/** Reuse a still-valid main-room credential across breakouts; Daily remains its validator. */
export function useMainRoomTarget({ meetingId, attendee, joinInfo, onJoinInfoRefresh }: Args) {
  const { isGuest, guestToken, guestSessionId, displayName, userId } = attendee
  const { url, token } = joinInfo
  const breakoutToken = joinInfo.breakout_token || attendee.breakoutToken
  // Scope both the credential and its pending refresh to this attendee in this meeting.
  const scope = useMemo(() => ({
    meetingId, isGuest, guestToken, guestSessionId, displayName, userId, breakoutToken,
    target: { url, token } as MainRoomTarget,
    pending: null as Promise<MainRoomTarget> | null,
    rejectedSession: null as Error | null,
  }), [meetingId, isGuest, guestToken, guestSessionId, displayName, userId, breakoutToken, url, token])

  const rejectedIdentityRef = useRef<{ meetingId: string; guestToken?: string | null; guestSessionId?: string | null; error: Error } | null>(null)
  const mountedRef = useRef(true)
  useEffect(() => { mountedRef.current = true; return () => { mountedRef.current = false } }, [])
  const currentScopeRef = useRef(scope)
  currentScopeRef.current = scope
  const onRefreshRef = useRef(onJoinInfoRefresh)
  onRefreshRef.current = onJoinInfoRefresh

  return useCallback((options?: MainRoomResolveOptions): Promise<MainRoomTarget> => {
    const rejected = rejectedIdentityRef.current
    if (rejected && scope.isGuest && rejected.meetingId === scope.meetingId && rejected.guestToken === scope.guestToken && rejected.guestSessionId === scope.guestSessionId) return Promise.reject(rejected.error)
    if (scope.rejectedSession) return Promise.reject(scope.rejectedSession)
    if (scope.pending) return scope.pending
    if (!options?.forceRefresh && hasReusableToken(scope.target.token)) return Promise.resolve(scope.target)
    // Tokenless/unknown credentials still go through the normal admission endpoint.
    scope.pending = (async () => {
      if (scope.isGuest) {
        if (!scope.guestToken || !scope.guestSessionId) throw new Error("Your guest session is unavailable. Please reopen your meeting link.")
        return meetings.joinGuestMeeting(scope.meetingId, {
          guest_token: scope.guestToken,
          guest_session_id: scope.guestSessionId,
          ...(scope.breakoutToken ? { breakout_token: scope.breakoutToken } : {}),
          display_name: scope.displayName,
        })
      }
      return meetings.joinMeeting(scope.meetingId)
    })().then(async (response) => {
      if (response.native_calls_enabled && requiresMeetingAdmission(response)) {
        const admitted = await meetings.prepareMobileAccess(scope.meetingId, {
          panel_token: getPanelTokenFromUrl(response.messages_panel?.url),
          ...(scope.guestToken ? { guest_token: scope.guestToken } : {}),
          ...(scope.breakoutToken ? { breakout_token: scope.breakoutToken } : {}),
        });
        if (admitted.state !== 'admitted' || !admitted.url || !admitted.token) throw new Error('Your meeting admission is no longer valid. Reopen the meeting to request access.');
        response = { ...response, url: admitted.url, token: admitted.token, requires_admission: false, poll_panel: admitted.poll_panel || response.poll_panel };
      }
      if (mountedRef.current && currentScopeRef.current === scope && scope.isGuest && scope.guestToken && "guest_session_id" in response && response.guest_session_id) {
        saveStoredGuestSession(scope.meetingId, scope.guestToken, { displayName: response.display_name || scope.displayName, guestSessionId: response.guest_session_id, breakoutToken: response.breakout_token || undefined })
      }
      scope.target = { url: response.url, token: response.token }
      // Recovery renews panel credentials and admission flags along with the Daily token.
      if (mountedRef.current && currentScopeRef.current === scope) onRefreshRef.current?.(response)
      return scope.target
    }).catch((error) => {
      if (scope.isGuest && scope.breakoutToken && error?.response?.status === 403) {
        // Keep this call tied to its original identity. Only reopening the link
        // may enter as a fresh guest, with ordinary admission and no old assignment.
        scope.rejectedSession = new Error(error?.response?.data?.detail || "Your guest session has expired. Reopen your meeting link.")
        rejectedIdentityRef.current = { meetingId: scope.meetingId, guestToken: scope.guestToken, guestSessionId: scope.guestSessionId, error: scope.rejectedSession }
        scope.breakoutToken = undefined
        scope.target = { url: scope.target.url, token: null }
        if (mountedRef.current && currentScopeRef.current === scope && scope.guestToken && scope.guestSessionId) {
          saveStoredGuestSession(scope.meetingId, scope.guestToken, { displayName: scope.displayName, guestSessionId: scope.guestSessionId })
        }
        throw scope.rejectedSession
      }
      throw error
    }).finally(() => { scope.pending = null })
    return scope.pending
  }, [scope])
}
